/**
 * キャリコネクション送客の「①媒体取得」結果をモジュール級にキャッシュする composable。
 * 出し分けは先方判定（①が返した媒体から varuna が 1 件選ぶ）。①は表示前に走る:
 *  - RT1.0: AUTO_RT（useRtOutputOrchestration が emit）を購読して先行フェッチ
 *  - DL RT: useDownloadItemAutoRt の選定ループから resolveCareerconnectionAutoRtInfo
 *    （useCareerconnectionAutoRtBanner）
 * 非対象が確定した RT1.0 行は未送信 close 相当（request_able=false）にし、DL RT には AUTO_RT を再 emit。
 * 行を物理削除しないのは、再生リストのカーソルがずれて次の他社 RT を飛ばすため。
 * キャッシュは uid × 条件行ごとに最新 1 件（書式の指紋付き。編集→再出力で再判定）。一時失敗は TTL で失効。
 * ②はここで①が判定したスナップショット（entry.snapshot）をそのまま送る。
 */
import {
  emitter,
  AUTO_RT,
  FORCE_HIDE_RT_DIALOGS,
  UPDATE_RT_CONDITION
} from '@/plugins/emitter'
import { postAlliances } from '@/services/careerconnection-client'
import {
  clearCcAutoRtArgs,
  getCcAutoRtArgs,
  rememberCcAutoRtArgs
} from '@/composables/useCareerconnectionAutoRtArgs'
import {
  clearCcSnapshotStoredCache,
  resolveSnapshot,
  resolveSnapshotSync,
  snapshotFingerprint
} from '@/composables/useCareerconnectionSnapshotSource'
import {
  buildCareerconnectionRequestBody,
  ccCacheKey,
  isCareerconnectionRow,
  resolveDeviceIsMobile,
  rowRefFromCondition,
  type CcFormats,
  type CcMedia,
  type CcRowRef
} from '@/utils/realtime-transfer/careerconnectionSnapshot'

type UnknownRecord = Record<string, any>

/** 判定に必要な最小の vm 形 */
export interface CcVmLike {
  $firebase: {
    user?: { uid?: string; getIdToken?: () => Promise<string> } | null
    isAuthenticated?: boolean
  }
  $deviceLib?: unknown
  $site?: { appFlag?: string }
  isMypage?: boolean
}

export type CcEligibilityStatus = 'pending' | 'eligible' | 'ineligible'

export interface CcEligibilityEntry {
  status: CcEligibilityStatus
  media: CcMedia | null
  reasons: string[]
  ref: CcRowRef
  dialogName: string
  /** ①に送ったスナップショット（②も同じものを送る） */
  snapshot: CcFormats
  /** 判定に使ったスナップショットの指紋 */
  fingerprint: string
  expiresAt: number | null // 過ぎたら未判定扱い（ms epoch）。eligible=50分 / 一時失敗=60秒 / 確定非対象=無期限
  transient: boolean // 一時的な非対象（先方エラー・timeout・未ログイン）。prune せず今回だけ見送る
  promise: Promise<CcEligibilityEntry>
}

export interface CcEligibilityContext {
  formatId: string
  basicData: UnknownRecord
  formatData?: unknown
  otherFormatData?: unknown
  dialogName?: string
  isMypage?: boolean
  autoRtArgs?: unknown[] // DL RT 側から呼ぶとき: 解決後に再 emit する AUTO_RT ペイロード
}

type AutoRtArgs = [unknown, UnknownRecord, string, unknown, unknown]

/** 一時的な失敗（先方エラー・タイムアウト・通信失敗）はこの時間だけ非対象として扱う */
const TRANSIENT_FAILURE_TTL_MS = 60 * 1000
const ELIGIBLE_TTL_MS = 50 * 60 * 1000 // varuna が①の媒体選択を②で受け付ける窓（60 分）より短く
// prettier-ignore
const TRANSIENT_REASONS = new Set(['gw_error', 'gw_timeout', 'request_failed', 'no_auth', 'state_unavailable'])

/** uid × 条件行 → 最新エントリ（指紋が変わったら差し替え） */
const cache = new Map<string, CcEligibilityEntry>()
let boundVm: CcVmLike | null = null
let subscribed = false
let forceHideGeneration = 0 // FORCE_HIDE_RT_DIALOGS の回数。await 前後で変わっていたら open 中断
let now: () => number = () => Date.now() // テストで差し替える

function uidOf(vm: CcVmLike | null): string {
  const uid = vm && vm.$firebase && vm.$firebase.user && vm.$firebase.user.uid
  return uid ? String(uid) : ''
}

/** Authorization 用の ID トークン。未ログイン / 取得失敗は空文字 */
async function idTokenOf(vm: CcVmLike): Promise<string> {
  const user = vm.$firebase && vm.$firebase.user
  if (!user || typeof user.getIdToken !== 'function') return ''
  try {
    return (await user.getIdToken()) || ''
  } catch (e) {
    console.error('[careerconnection] IDトークンの取得に失敗:', e)
    return ''
  }
}

const onForceHide = () => {
  forceHideGeneration += 1
  clearCcAutoRtArgs()
  clearCcSnapshotStoredCache()
}

function onAutoRt(args: unknown) {
  if (!Array.isArray(args) || !boundVm) return
  rememberCcAutoRtArgs(args)
  const [conditions, basicData, formatId, formatData, otherFormatData] =
    args as AutoRtArgs
  const list = Array.isArray(conditions)
    ? conditions
    : conditions && typeof conditions === 'object'
      ? Object.values(conditions as Record<string, unknown>)
      : []
  // 先行フェッチは RT1.0 に出る行だけ（auto_rt 行は DL RT 側が必要時に①を起動）。request_able=false は叩かない
  const ccRows = list.filter(
    (row) =>
      isCareerconnectionRow(row) &&
      (row as UnknownRecord).dialog_name !== 'auto_rt' &&
      (row as UnknownRecord).request_able !== false
  ) as UnknownRecord[]
  if (ccRows.length === 0) return
  for (const row of ccRows) {
    ensureEligibility(boundVm, rowRefFromCondition(row), {
      formatId: String(formatId || ''),
      basicData: (basicData || {}) as UnknownRecord,
      formatData,
      otherFormatData,
      dialogName: String(row.dialog_name || ''),
      isMypage: boundVm.isMypage === true
    })
  }
}

function subscribeOnce() {
  if (subscribed) return
  subscribed = true
  emitter.on(AUTO_RT, onAutoRt)
  emitter.on(FORCE_HIDE_RT_DIALOGS, onForceHide)
}

/** ダイアログの setup から呼ぶ。以後 AUTO_RT で先行フェッチが走る（購読は 1 回・vm は最後の bind） */
export function bindCareerconnectionVm(vm: CcVmLike): void {
  boundVm = vm
  subscribeOnce()
}

export function getForceHideGeneration(): number {
  return forceHideGeneration
}

function isExpired(entry: CcEligibilityEntry): boolean {
  return entry.expiresAt != null && entry.expiresAt <= now()
}

/** 現在のエントリ。指紋が保存済みと異なる / TTL 切れなら「未判定」（null） */
export function peekEligibility(
  vm: CcVmLike | null,
  ref: CcRowRef,
  fingerprint?: string
): CcEligibilityEntry | null {
  const entry = cache.get(ccCacheKey(uidOf(vm), ref))
  if (!entry) return null
  if (fingerprint != null && entry.fingerprint !== fingerprint) return null
  if (isExpired(entry)) return null
  return entry
}

function settle(
  key: string,
  entry: CcEligibilityEntry,
  status: Exclude<CcEligibilityStatus, 'pending'>,
  media: CcMedia | null,
  reasons: string[]
) {
  entry.status = status
  entry.media = media
  entry.reasons = reasons
  entry.transient =
    status === 'ineligible' && reasons.some((r) => TRANSIENT_REASONS.has(r))
  const ttl = entry.transient ? TRANSIENT_FAILURE_TTL_MS : ELIGIBLE_TTL_MS
  entry.expiresAt =
    entry.transient || status === 'eligible' ? now() + ttl : null
  // 差し替え済み（新しい指紋）の in-flight 結果は副作用なし。prune はサーバー確定の非対象だけ
  if (cache.get(key) !== entry) return
  if (
    status === 'ineligible' &&
    !entry.transient &&
    entry.dialogName !== 'auto_rt'
  ) {
    // 未送信 close と同じ 5 引数: FormatEdit / マイページは request_able=false（チェーン中は選ばない）。
    // DL 行の非対象は RT1.0 行に波及させない（DL 側は選定ループで次の行へ進む）
    emitter.emit(UPDATE_RT_CONDITION, [
      entry.ref.clientId,
      entry.ref.permissionType,
      false,
      entry.dialogName,
      entry.ref.conditionId
    ])
  }
  // DL RT: バナー選定をやり直させる（cache 済みなので再フェッチは起きない）
  const args = getCcAutoRtArgs()
  if (args) emitter.emit(AUTO_RT, args)
}

/**
 * ①を（未実行なら）実行し、結果エントリを返す。同じ uid × 条件行 × 指紋は 1 回だけ叩く。
 * 指紋が変わっていれば（履歴書を編集した）古いエントリを捨てて再判定する。
 * 通信例外・timeout・eligible:false はすべて「非対象」として確定する。
 */
export async function ensureEligibility(
  vm: CcVmLike,
  ref: CcRowRef,
  ctx: CcEligibilityContext
): Promise<CcEligibilityEntry> {
  if (Array.isArray(ctx.autoRtArgs)) rememberCcAutoRtArgs(ctx.autoRtArgs)
  const key = ccCacheKey(uidOf(vm), ref)
  const syncSnapshot = resolveSnapshotSync(ctx)
  const snapshot = syncSnapshot || (await resolveSnapshot(ctx))
  const fingerprint = snapshotFingerprint(snapshot)
  const existing = cache.get(key)
  if (
    existing &&
    existing.fingerprint === fingerprint &&
    !isExpired(existing)
  ) {
    // 保存値を読んで初めて指紋が決まった: DL バナーは未確定として出していないので選定をやり直させる
    const args = getCcAutoRtArgs()
    if (!syncSnapshot && existing.status !== 'pending' && args) {
      emitter.emit(AUTO_RT, args)
    }
    return existing.promise
  }

  const entry = {
    status: 'pending',
    media: null,
    reasons: [],
    ref,
    dialogName: String(ctx.dialogName || ''),
    snapshot,
    fingerprint,
    expiresAt: null,
    transient: false
  } as Omit<CcEligibilityEntry, 'promise'> as CcEligibilityEntry
  entry.promise = (async () => {
    try {
      const idToken = await idTokenOf(vm)
      if (!idToken) {
        // 未ログインは varuna が 401 を返すので呼ばずに非対象（一時扱い）
        settle(key, entry, 'ineligible', null, ['no_auth'])
        return entry
      }
      const body = buildCareerconnectionRequestBody(
        {
          uuid: uidOf(vm),
          clientId: ref.clientId,
          permissionType: ref.permissionType,
          conditionId: ref.conditionId,
          formatId: ctx.formatId,
          dialogName: ctx.dialogName || '',
          isMobile: resolveDeviceIsMobile(vm.$deviceLib),
          appFlag: String((vm.$site && vm.$site.appFlag) || ''),
          isMypage: ctx.isMypage === true
        },
        snapshot
      )
      const res = await postAlliances(body, idToken)
      if (res.eligible && res.media) {
        settle(key, entry, 'eligible', res.media, [])
      } else {
        settle(
          key,
          entry,
          'ineligible',
          null,
          res.reasons.length ? res.reasons : ['ineligible']
        )
      }
    } catch (error) {
      console.warn('[careerconnection] alliances failed', error)
      settle(key, entry, 'ineligible', null, ['request_failed'])
    }
    return entry
  })()
  cache.set(key, entry)
  return entry.promise
}

/** open 時の待ち合わせ。timeoutMs 超過は 'timeout'（cache は pending のまま残し DL RT が結果を受け取る） */
export async function awaitEligibility(
  vm: CcVmLike,
  ref: CcRowRef,
  ctx: CcEligibilityContext,
  timeoutMs = 8000
): Promise<{
  status: CcEligibilityStatus | 'timeout'
  media: CcMedia | null
  transient: boolean // true なら行を prune しない（timeout / 一時的な失敗）
}> {
  const promise = ensureEligibility(vm, ref, ctx)
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  const result = await Promise.race([promise, timeout])
  if (timer) clearTimeout(timer)
  if (result === 'timeout')
    return { status: 'timeout', media: null, transient: true }
  return {
    status: result.status,
    media: result.media,
    transient: result.transient
  }
}

/** テスト用: 状態を初期化 */
export function __resetCareerconnectionEligibilityForTest(
  clock?: () => number
): void {
  cache.clear()
  clearCcAutoRtArgs()
  clearCcSnapshotStoredCache()
  forceHideGeneration = 0
  now = clock || (() => Date.now())
  if (subscribed) {
    emitter.off(AUTO_RT, onAutoRt)
    emitter.off(FORCE_HIDE_RT_DIALOGS, onForceHide)
  }
  subscribed = false
  boundVm = null
}
